import { useState, useMemo } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';

const PERIODS = {
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};

const TEMP_MIN = 2;
const TEMP_MAX = 8;

const TemperatureChart = ({ deviceId }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const [period, setPeriod] = useState('24h');
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffectAsync(async () => {
    if (!deviceId) return;

    setLoading(true);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - PERIODS[period]);

      const params = new URLSearchParams({
        deviceId,
        from: from.toISOString(),
        to: to.toISOString(),
      });

      const response = await fetch(`/api/positions?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPositions(data);
      }
    } finally {
      setLoading(false);
    }
  }, [deviceId, period]);

  const chartData = useMemo(() => {
    return positions
      .filter((pos) => {
        const temp = pos.attributes?.temp1 ?? pos.attributes?.temp ?? pos.attributes?.temperature;
        return temp !== undefined && temp !== null;
      })
      .map((pos) => {
        const temp = pos.attributes?.temp1 ?? pos.attributes?.temp ?? pos.attributes?.temperature;
        return {
          time: new Date(pos.fixTime).getTime(),
          temperature: temp,
          formattedTime: new Date(pos.fixTime).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          formattedDate: new Date(pos.fixTime).toLocaleDateString(),
        };
      })
      .sort((a, b) => a.time - b.time);
  }, [positions]);

  const minTemp = useMemo(() => {
    if (chartData.length === 0) return -10;
    const min = Math.min(...chartData.map((d) => d.temperature));
    return Math.floor(min - 5);
  }, [chartData]);

  const maxTemp = useMemo(() => {
    if (chartData.length === 0) return 20;
    const max = Math.max(...chartData.map((d) => d.temperature));
    return Math.ceil(max + 5);
  }, [chartData]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: tokens.surface.card,
            border: `1px solid ${tokens.border.default}`,
            borderRadius: tokens.radius.md,
            p: 1.5,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" fontWeight="bold" sx={{ color: tokens.text.primary }}>
            {data.temperature?.toFixed(1)}°C
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
            {data.formattedDate} {data.formattedTime}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <ToggleButtonGroup
          size="small"
          value={period}
          exclusive
          onChange={(e, v) => v && setPeriod(v)}
        >
          <ToggleButton value="1h">1h</ToggleButton>
          <ToggleButton value="6h">6h</ToggleButton>
          <ToggleButton value="24h">24h</ToggleButton>
          <ToggleButton value="7d">7d</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <LinearProgress />
      ) : chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={['auto', 'auto']}
              tickFormatter={(tick) => {
                const date = new Date(tick);
                if (period === '7d') {
                  return date.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
                }
                return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              }}
            />
            <YAxis
              domain={[minTemp, maxTemp]}
              tickFormatter={(value) => `${value}°C`}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Ideal temperature range */}
            <ReferenceArea
              y1={TEMP_MIN}
              y2={TEMP_MAX}
              fill={tokens.semantic.success}
              fillOpacity={0.1}
              label={{ value: 'Faixa Ideal', position: 'insideTopLeft', fill: tokens.semantic.success }}
            />

            {/* Reference lines for limits */}
            <ReferenceLine
              y={TEMP_MIN}
              stroke={tokens.semantic.success}
              strokeDasharray="5 5"
              label={{ value: `Min: ${TEMP_MIN}°C`, position: 'left', fill: tokens.semantic.success }}
            />
            <ReferenceLine
              y={TEMP_MAX}
              stroke={tokens.semantic.success}
              strokeDasharray="5 5"
              label={{ value: `Max: ${TEMP_MAX}°C`, position: 'left', fill: tokens.semantic.success }}
            />

            <Line
              type="monotone"
              dataKey="temperature"
              stroke={tokens.semantic.info}
              strokeWidth={2}
              dot={chartData.length < 50}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Box textAlign="center" py={4}>
          <Typography sx={{ color: tokens.text.secondary }}>
            {t('sharedNoData')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TemperatureChart;
